import templateUrl from './sidebar-widget.html'
import './sidebar-widget.scss'

const LEGACY_CHANNEL = 'legacy'
const TEAM_MEMBER_ADDED_EVENT = 'ad-hoc-team-member-added'
const TEAM_MEMBER_REMOVED_EVENT = 'ad-hoc-team-member-removed'

const ProjectTeamSidebarWidgetComponent = {
  bindings: {
    project: '<'
  },
  templateUrl,
  controller: class ProjectTeamSidebarWidget {
    constructor ($scope, $state, Configuration, ProjectTeamService, UserService, ModalService, EventBusService) {
      'ngInject'
      this._identify = 'ProjectTeamSidebarWidgetComponent'
      this.$scope = $scope
      this.$state = $state
      this.Configuration = Configuration
      this.ProjectTeamService = ProjectTeamService
      this.UserService = UserService
      this.ModalService = ModalService
      this.EventBusService = EventBusService
    }

    $onInit () {
      this.team = null
      this.isLoading = false

      // Load team for expert and client
      this.loadTeam()
      this.bindRealtimeEvents()
    }

    $onDestroy () {
      this._destroyed = true
      this.unbindRealtimeEvents()
    }

    // Pusher events for the project's task-owner channel are forwarded by the legacy
    // Sockets service onto the 'legacy' EventBus channel (camelCased payloads). We
    // consume them there — the same proven path the activity stream uses — instead of
    // binding the raw Pusher channel, which the legacy service can unbind on retry.
    bindRealtimeEvents () {
      this.subscriptionCollection = this.EventBusService.createSubscriptionCollection(LEGACY_CHANNEL)

      this.subscriptionCollection.subscribe(TEAM_MEMBER_ADDED_EVENT, (event) => {
        if (!this.isForThisProject(event)) return
        this.$scope.$applyAsync(() => this.loadTeam())
      })

      this.subscriptionCollection.subscribe(TEAM_MEMBER_REMOVED_EVENT, (event) => {
        if (!this.isForThisProject(event)) return
        this.$scope.$applyAsync(() => {
          this.loadTeam()
          this.handleMemberRemoved(event && event.data)
        })
      })
    }

    unbindRealtimeEvents () {
      if (this.subscriptionCollection) {
        this.subscriptionCollection.unsubscribeAll()
        this.subscriptionCollection = null
      }
    }

    // The 'legacy' bus carries events for every channel the user is subscribed to,
    // so only react to events for the workroom this widget belongs to.
    isForThisProject (event) {
      if (this._destroyed) return false
      const projectId = event && event.projectId
      return !projectId || projectId === this.project.id
    }

    // When the current expert is the one whose membership/workroom access was just
    // revoked, they can no longer see this workroom — bounce them out. Pusher keeps
    // delivering on an already-subscribed channel even after server-side auth changes,
    // so this fires reliably for an expert actively viewing the workroom.
    handleMemberRemoved (data) {
      const removedExpertId = data && data.expert && data.expert.id
      const currentUserId = this.UserService.user && this.UserService.user.id
      const isCurrentUser = !!removedExpertId && !!currentUserId && removedExpertId === currentUserId

      if (isCurrentUser && this.UserService.isExpert() && !this.isTeamLead) {
        this.evictFromWorkroom()
      }
    }

    evictFromWorkroom () {
      if (this._evicted) return
      this._evicted = true

      const redirect = () => {
        this.$state.go(
          this.Configuration.defaultRouterState.name,
          this.Configuration.defaultRouterState.options
        )
      }

      const config = this.ModalService.generateConfig('default', {
        title: 'Workroom access removed',
        body: 'The team lead has removed your access to this project workroom. You no longer have access to its details.',
        closeOnBackdrop: false,
        closeIcon: false,
        layout: 'center',
        resolveButton: { label: 'OK', class: 'button-primary' }
      })

      this.ModalService.open(config).then(redirect)
    }

    loadTeam () {
      // load team data
      this.isLoading = true
      this.ProjectTeamService.fetchTeam(this.project.id)
        .then(response => {
          this.team = response
        })
        .finally(() => {
          this.isLoading = false
        })
    }

    get hasTeamMembers () {
      return this.team && this.team.teamMembers.length
    }

    get isTeamLead () {
      const userId = this.UserService.user && this.UserService.user.id
      const leadId = this.team && this.team.teamLead && this.team.teamLead.expert && this.team.teamLead.expert.id
      return !!userId && !!leadId && userId === leadId
    }

    updateMembers ($event) {
      this.team.teamMembers.push(...$event.members)
    }

    onRemoveMember ($event) {
      console.log('[ProjectTeamSidebarWidgetComponent] > onRemoveMember', $event)
      if ($event && $event.member) {
        const memberIndex = this.team.teamMembers.findIndex(member => member.id === $event.member.id)
        console.log('[ProjectTeamSidebarWidgetComponent] > onRemoveMember > memberIndex', memberIndex)
        if (memberIndex > -1) {
          this.team.teamMembers.splice(memberIndex, 1)
        }
      }
    }

    onVisibilityChanged ($event) {
      if (!$event || !$event.member) return
      const existing = this.team.teamMembers.find(m => m.id === $event.member.id)
      if (existing) {
        existing.workroomVisible = $event.member.workroomVisible
      }
    }

    onPaymentCreated ($event) {
      console.log('[ProjectTeamSidebarWidgetComponent] > onPaymentCreated > 1')
      // update member who recevied payment
      if ($event.payment) {
        const member = this.team.teamMembers.find(m => m.id === $event.payment.teamMember.id)
        if (member) {
          Object.assign(member, $event.payment.teamMember)
          console.log('[ProjectTeamSidebarWidgetComponent] > onPaymentCreated > 2')
        }
      }
    }


    openManageModal () {
      const vm = this
      this.ModalService.open({
        closeOnBackdrop: true,
        closeIcon: true,
        layout: 'center',
        class: 'project-team-manage-modal',
        template: `<cdbl-project-team-manage-modal project="$ctrl.modal.project" team="$ctrl.modal.team" on-members-added="$ctrl.modal.updateMembers($event)"></cdbl-project-team-manage-modal>`,
        project: this.project,
        team: this.team,
        updateMembers: $event => {
          vm.updateMembers($event)
        }
      })
    }

    openPaymentsModal () {
      const vm = this
      this.ModalService.open({
        closeOnBackdrop: true,
        closeIcon: true,
        class: 'project-team-payments-modal',
        template: `<cdbl-project-team-payments-modal project="$ctrl.modal.project" team="$ctrl.modal.team" on-payment-created="$ctrl.modal.onPaymentCreated($event)"></cdbl-project-team-payments-modal>`,
        project: this.project,
        team: this.team,
        onPaymentCreated: $event => {
          vm.onPaymentCreated($event)
        }
      })
    }
  }
}

export default ProjectTeamSidebarWidgetComponent
